import { Checkbox } from "@/components/ui/checkbox"
import { CircularProgress } from "@/components/ui/circular-progress"
import { useAuth } from "@/modules/auth/store"
import { useCosmos } from "@/modules/cosmos/stores/use-cosmos-store"
import type { Feedback } from "@/services/api/odin/activities/flashcards/study-session/update"
import mixpanelClient from "@/services/mixpanel"
import { useEffect } from "react"

interface FlashcardFeedbackProps {
  feedback: Feedback
}

export default function FlashcardFeedback({ feedback }: FlashcardFeedbackProps) {
  const { user } = useAuth()
  const { cosmosKey } = useCosmos()
  const isApproved = feedback.scorePercentage >= feedback.approvalScore

  useEffect(() => {
    mixpanelClient.activities.end({
      name: `${user.firstName} ${user.lastName}`,
      email: user.emailAddress,
      approved: isApproved,
      endDate: new Date().toISOString(),
      score: feedback.approvalScore,
      organization: cosmosKey,
    })
  }, [])

  return (
    <div className="mx-auto w-full max-w-3xl rounded-lg">
      <div className="relative p-2">
        <div className="mb-8 flex items-center justify-center">
          <CircularProgress size="xxl" progress={feedback.scorePercentage} />
        </div>
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-2xl font-bold text-primary">
            {isApproved ? "¡Increíble! 🎉" : "¡Ya casi! 💪"}
          </h2>
          <p className="text-md font-semibold text-neutral-600">
            {isApproved
              ? "¡Aprobaste la actividad! ¡Vamos por más!"
              : `Alcanza un ${feedback.approvalScore}% de efectividad para aprobar la actividad.`}
          </p>
        </div>
        <div>
          <h3 className="mb-3 text-sm font-bold text-primary">
            {feedback.scorePercentage === 100 ? "Fortalezas:" : "¿Qué podés mejorar?"}
          </h3>
          <ul className="space-y-3">
            {feedback.bullets.map((point, index) => (
              <li key={index} className="flex items-center gap-2">
                <Checkbox id={`feedback-${index}`} checked />
                <label
                  htmlFor={`feedback-${index}`}
                  className="cursor-pointer text-sm font-medium text-neutral-800"
                >
                  {point}
                </label>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
